import React, { useState, useEffect } from 'react';
import { Button } from '../common/Button';
import FormattedCurrency from '../common/FormattedCurrency';
import { UpsertQuoteModalActions } from './UpsertQuoteModalActions';
import { Trans, useTranslation } from 'react-i18next';
import { QuoteJob, Quote, QuoteAlliance, QuoteOwner } from '../../types/quote';
import { useQuoteStore } from '../../stores/quoteStore';
import FullBlockingModal from '../common/FullBlockingModal';
import { Job } from '../../types/job';
import MyAlliances from './MyAlliances';
import { computeQuoteAmount, computeQuoteTotalAmount } from '../../utils/jobUtils';
import WalletSummary from '../credits/WalletSummary';
import { useCreditStore } from '../../stores/creditStore';

interface MainSubmissionProps {
  job: QuoteJob | Job;
  quote?: Quote;
  parentQuoteOwner?: QuoteOwner | null;
  parentQuoteId?: string | null;
  onClose: () => void;
  hasUnsavedOfferChanges?: boolean;
  onSaveOffer?: () => Promise<boolean>;
}

const MainSubmission: React.FC<MainSubmissionProps> = ({
  job,
  quote,
  parentQuoteOwner,
  onClose,
  hasUnsavedOfferChanges = false,
  onSaveOffer,
}) => {
  const { t } = useTranslation('quotes');

  const { submitQuote } = useQuoteStore();
  const { balance, getActionCost, refreshBalance, fetchActionCosts, actionCosts } =
    useCreditStore();

  const [submitting, setSubmitting] = useState(false);
  const [savingOffer, setSavingOffer] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showSubmitConfirmation, setShowSubmitConfirmation] = useState(false);

  const isPublicJob = job.visibility === 'public';
  const submitPublicJobQuoteCost = getActionCost('submit_public_job_quote');

  // A pending change-request resubmission is free server-side (submit_quote
  // skips credit validation/consumption for it) -- the credit gate below
  // must not block it either, otherwise a freelancer with zero balance who's
  // mid-adjustment can never click Submit at all.
  const isAdjustmentResubmit =
    quote?.status === 'submitted' && !!quote?.change_request_message?.trim();

  const hasEnoughCredits =
    !isPublicJob ||
    isAdjustmentResubmit ||
    (balance !== null && balance >= submitPublicJobQuoteCost);

  useEffect(() => {
    refreshBalance();
    // Fetch action costs if not already loaded
    if (!Object.keys(actionCosts).length) {
      fetchActionCosts();
    }
  }, [actionCosts, fetchActionCosts, refreshBalance]);

  const tasks = quote?.tasks ?? [];
  const hasAtLeastOneTaskWithTitle = tasks.some(task => task.name && task.name.trim().length > 0);
  const quoteAlliances = quote?.alliances ?? [];
  const hasAlliances = quoteAlliances.length > 0;
  const hasChildQuote = (a: QuoteAlliance) => a.child_quote !== null && a.child_quote !== undefined;
  const allAlliancesSubmitted =
    hasAlliances &&
    quoteAlliances.length > 0 &&
    quoteAlliances.every(a => hasChildQuote(a) && a.child_quote?.status === 'submitted');

  const hasTasks = tasks.length > 0 && hasAtLeastOneTaskWithTitle;

  const canModifyOrSubmitQuote = quote?.status === 'draft' || isAdjustmentResubmit;

  let canSubmit = false;
  let submitDisabledReason = '';
  if (!hasTasks) {
    canSubmit = false;
    submitDisabledReason = t(
      'createQuoteModal.submitDisabledNoOffer',
      'Vous devez ajouter votre offre pour envoyer votre soumission.'
    );
  } else if (hasAlliances && !allAlliancesSubmitted) {
    canSubmit = false;
    submitDisabledReason = t(
      'createQuoteModal.submitDisabledAlliances',
      'Tous les membres de l’alliance doivent soumettre leur offre pour envoyer la soumission.'
    );
  } else {
    canSubmit = true;
  }

  const handleSubmitQuote = async () => {
    if (!quote?.id) return;
    try {
      setSubmitting(true);
      await submitQuote(quote.id);
      setShowSubmitConfirmation(true);
    } catch (error) {
      console.error('Error submitting quote:', error);
      const errorMessage = (error as { message: string }).message;
      if (errorMessage.includes('QUOTE_ALLIANCE_RETIRED')) {
        setSaveError(
          t(
            'errors.quoteAllianceRetired',
            'Vous ne pouvez plus soumettre cette soumission car votre invitation a été annulée.'
          )
        );
      } else if (errorMessage.includes('JOB_NOT_AVAILABLE')) {
        setSaveError(
          t(
            'errors.jobNotAvailable',
            'Vous ne pouvez plus soumettre cette soumission car le projet associé a été annulé.'
          )
        );
      } else if (errorMessage.includes('INSUFFICIENT_CREDITS_ERROR')) {
        setSaveError(
          t(
            'errors.insufficientCredits',
            'Crédits insuffisants pour soumettre cette soumission. Veuillez acheter des crédits.'
          )
        );
        await refreshBalance();
      } else {
        setSaveError(
          t('errors.submitError', "Une erreur est survenue lors de l'envoi de la soumission")
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveOffer = async () => {
    setSavingOffer(true);
    setSaveError(null);
    try {
      await onSaveOffer?.();
    } finally {
      setSavingOffer(false);
    }
  };

  const primaryLabel = hasUnsavedOfferChanges
    ? savingOffer
      ? t('createQuoteModal.savingButtonLabel', 'Enregistrement...')
      : t('common:form_actions.save', 'Enregistrer')
    : submitting
      ? t('createQuoteModal.submittingButtonLabel', 'Envoi en cours...')
      : isPublicJob && !isAdjustmentResubmit
        ? t(
            'createQuoteModal.submitQuoteWithCreditButtonLabel',
            'Utiliser 1 jeton et envoyer la soumission',
            { count: submitPublicJobQuoteCost }
          )
        : t('createQuoteModal.submitQuoteButtonLabel', 'Envoyer la soumission');

  const primaryDisabled = hasUnsavedOfferChanges
    ? savingOffer
    : !canSubmit || submitting || !canModifyOrSubmitQuote || !hasEnoughCredits;

  const submissionButtons = [
    {
      label: primaryLabel,
      variant: 'primary' as const,
      onClick: async () => {
        if (hasUnsavedOfferChanges) {
          await handleSaveOffer();
        } else {
          await handleSubmitQuote();
        }
      },
      className: 'w-full md:w-auto md:px-8',
      type: 'button' as const,
      disabled: primaryDisabled,
    },
    {
      label: t('common:form_actions.close'),
      variant: 'simple-text' as const,
      onClick: onClose,
      className: 'w-full md:w-auto md:px-8',
      type: 'button' as const,
    },
  ];

  const myQuoteTotal = quote ? computeQuoteAmount(quote) : 0;
  const totalAmount = quote ? computeQuoteTotalAmount(quote) : 0;

  return (
    <div className="space-y-6">
      {tasks.length > 0 && (
        <div className="flex items-center justify-between w-full py-2">
          <span className="text-3xl font-bold text-primary">
            <FormattedCurrency value={myQuoteTotal} />
          </span>
        </div>
      )}

      {!parentQuoteOwner && canModifyOrSubmitQuote && (
        <>
          <MyAlliances quote={quote} />
          <hr className="border-[#E1E9F0]" />
        </>
      )}

      <UpsertQuoteModalActions
        myButtons={submissionButtons}
        saveError={saveError}
        canSubmit={hasUnsavedOfferChanges ? true : canSubmit}
        submitDisabledReason={
          !hasEnoughCredits && isPublicJob
            ? t('createQuoteModal.submitDisabledInsufficientCredits', {
                count: submitPublicJobQuoteCost,
                defaultValue:
                  'Crédits insuffisants. {{count}} jeton(s) requis pour soumettre une soumission à un projet public.',
              })
            : submitDisabledReason
        }
        totalAmount={totalAmount}
        totalAmountLabel={t('createQuoteModal.totalAmountLabel', 'Grand total')}
        showTotalAmount={!parentQuoteOwner}
      />

      {isPublicJob && (
        <div className="mt-4">
          <WalletSummary
            outerClassName="w-full"
            modalParams={
              quote
                ? {
                    type: 'quote',
                    quoteId: quote.id,
                  }
                : undefined
            }
          />
        </div>
      )}

      {showSubmitConfirmation && (
        <FullBlockingModal innerClassName="md:max-w-xl">
          <h2 className="mb-4 text-xl font-bold text-secondary">
            {t('createQuoteModal.submitConfirmationTitle')}
          </h2>
          <p className="mb-4 text-secondary">
            <Trans
              i18nKey="createQuoteModal.submitConfirmationText"
              ns="quotes"
              components={{ bold: <span className="font-bold" /> }}
              values={{
                requesterName: parentQuoteOwner
                  ? parentQuoteOwner.first_name
                  : job.client.first_name,
              }}
            />
          </p>
          <div className="mt-4 flex justify-end gap-4">
            <Button
              variant="primary"
              className="w-full md:w-auto md:px-8"
              onClick={() => {
                setShowSubmitConfirmation(false);
                onClose();
              }}
            >
              {t('createQuoteModal.backToSubmissionsButtonLabel')}
            </Button>
          </div>
        </FullBlockingModal>
      )}
    </div>
  );
};

export default MainSubmission;
